import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import localStore from '@shared/utils/localStore.ts';
import { customAlphabet } from 'nanoid';

import type { TokensDto } from '../services/authApi.ts';
import type { UserDto } from '../services/usersApi.ts';

interface AuthState {
  tokens?: { accessToken: string | null; refreshToken: string | null };
  isAuthenticated: boolean;
  sessionGeneration: number;
  isLoading: boolean;
  user?: UserDto;
  userId?: number | string;
  permissions?: string[];
}

// Define root state interface for this slice
interface RootStateWithAuth {
  auth: AuthState;
  [key: string]: unknown; // Allow for other slices
}

const initialState: AuthState = {
  tokens: localStore.getTokens(),
  isAuthenticated: localStore.getTokens().accessToken !== null,
  sessionGeneration: 0,
  isLoading: false,
  user: undefined,
  userId: localStore.getUserId(),
  permissions: [],
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    login: (state, action: PayloadAction<TokensDto>) => {
      const { accessToken, refreshToken, userId } = action.payload;
      localStore.setTokens({ accessToken, refreshToken });
      localStore.setUserId(userId);
      state.tokens = { accessToken, refreshToken };
      state.userId = userId;
      state.isAuthenticated = true;
      state.sessionGeneration += 1;
      state.isLoading = false;
      state.user = undefined;
      state.permissions = [];
    },
    logout: state => {
      state.tokens = undefined;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.permissions = [];
      state.userId = undefined;
      state.user = undefined;

      localStore.clearTokens();
      localStore.deleteUserId();
    },
    getUser: (state, action: PayloadAction<UserDto>) => {
      state.user = action.payload;
      state.userId = action.payload.id;
      state.isLoading = false;
    },
    loading: state => {
      state.isLoading = true;
    },
    updateTokens: (state, action: PayloadAction<TokensDto>) => {
      const { accessToken, refreshToken, userId } = action.payload;
      localStore.setTokens({ accessToken, refreshToken });
      localStore.setUserId(userId);
      state.tokens = { accessToken, refreshToken };
      state.userId = userId;
    },
    setUserId: (state, action: PayloadAction<number>) => {
      state.userId = action.payload;
      localStore.setUserId(action.payload);
    },
    setPublicUserId: state => {
      const randUserId = customAlphabet(
        '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz',
        21,
      )();
      state.userId = randUserId;
      localStore.setUserId(randUserId);
    },
    setPermissions: (state, action: PayloadAction<string[]>) => {
      state.permissions = action.payload;
    },
  },
});

export const {
  login,
  logout,
  getUser,
  loading,
  updateTokens,
  setUserId,
  setPublicUserId,
  setPermissions,
} = authSlice.actions;

export const selectAuthUserId = (state: RootStateWithAuth) => state.auth.userId;

export const selectAuthUser = (state: RootStateWithAuth) => state.auth.user;

export const selectAuthUserLanguage = () => {
  return localStore.getLanguage() || 'cn';
};

export const selectIsAuthenticated = (state: RootStateWithAuth) => state.auth.isAuthenticated;

export const selectPermissions = (state: RootStateWithAuth) => state.auth.permissions || [];

export const selectUserId = createSelector([selectAuthUserId, selectAuthUser], (userId, user) => {
  if (userId) {
    return userId;
  }
  return user?.id;
});

export default authSlice.reducer;
